import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import {
  isGameViewedInCache,
  markGameViewedInCache,
} from './gameViewedCache';
import { fetchViewedGameIds } from './replayApi';

type GameViewedInput = {
  gameId: string;
  viewedAt?: string;
};

export function useGameViewedStatus(games: ReadonlyArray<GameViewedInput>): Set<string> {
  const { isLoggedIn, user } = useAuth();
  const userId = user?.id;
  const [fetchedViewed, setFetchedViewed] = useState<Set<string>>(() => new Set());

  const gameIdsKey = useMemo(
    () => games.map((game) => game.gameId).join('\0'),
    [games],
  );

  useEffect(() => {
    const viewed = new Set<string>();
    const toFetch: string[] = [];

    for (const game of games) {
      if (game.viewedAt) {
        viewed.add(game.gameId);
        markGameViewedInCache(game.gameId);
      } else if (isGameViewedInCache(game.gameId)) {
        viewed.add(game.gameId);
      } else {
        toFetch.push(game.gameId);
      }
    }

    setFetchedViewed(viewed);

    if (!isLoggedIn || toFetch.length === 0) {
      return;
    }

    let cancelled = false;
    void fetchViewedGameIds(toFetch).then((ids) => {
      if (cancelled) {
        return;
      }
      for (const id of ids) {
        markGameViewedInCache(id);
      }
      setFetchedViewed((prev) => {
        const next = new Set(prev);
        for (const id of ids) {
          next.add(id);
        }
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [gameIdsKey, games, isLoggedIn, userId]);

  return fetchedViewed;
}
